import React, { useContext, useEffect } from 'react'
import { Stage, Layer, Line } from 'react-konva'

import isEqual from 'lodash/isEqual'

import { useMutation } from 'components/graphql'
import { mutation } from '../../helpers'

import { rawUpdateFurniture, rawDeleteFurniture, rawCreateFurniture } from '../../mutations'
import { RestaurantContext } from '../../RestaurantDesignerHandler'

import Table from './Table'
import Interior from './Interior'
import { StyledDnDContainer } from './styled/common'
import ContextMenu from './ContextMenu'
import Portal from './Portal'

const Container = () => {
	const { currentProjectData, setCurrentProjectData, activeFurnitureId, setActiveFurnitureId, setShapeCreatorType } = useContext(RestaurantContext)
	const [tables, setTables] = React.useState(currentProjectData.roomData.furniture.filter(el => el.objectType === 'table'))
	const [interior, setInterior] = React.useState(currentProjectData.roomData.furniture.filter(el => (el.objectType === 'object') && (el.objectKind)))
	const [selectedContextMenu, setSelectedContextMenu] = React.useState(null)
	const [updating, setUpdating] = React.useState(false)
	const [cloneFurniture] = useMutation(rawCreateFurniture)
	const [updateFurniture] = useMutation(rawUpdateFurniture)
	const [deleteFurniture] = useMutation(rawDeleteFurniture)

	useEffect(() => {
		const newTables = currentProjectData.roomData.furniture.filter(el => ((el.objectType === 'object') && (el.objectKind === 'table')))
		if (!isEqual(tables, newTables) && !updating) {
			setTables(newTables)
		}
		const newInterior = currentProjectData.roomData.furniture.filter(el => (el.objectType === 'object') && (el.objectKind !== 'table'))
		if (!isEqual(interior, newInterior) && !updating) {
			setInterior(newInterior)
		}
	}, [currentProjectData.roomData.id, currentProjectData.roomData.furniture])

	const gridSize = 20
	const canvasWidth = 1280
	const canvasHeight = 900
	const linesA = []
	const linesB = []

	for (let i = 0; i < canvasWidth / gridSize; i++) {
		linesA.push(
			<Line
				key={i}
				strokeWidth={2}
				stroke='#ffffff40'
				points={[i * gridSize, 0, i * gridSize, canvasWidth]}
			/>
		)

		linesB.push(
			<Line
				key={i}
				strokeWidth={2}
				stroke='#ffffff40'
				points={[0, i * gridSize, canvasWidth, i * gridSize]}
			/>
		)
	}

	const checkDeselect = (e) => {
		// deselect when clicked on empty area
		const clickedOnEmpty = e.target === e.target.getStage()
		if (clickedOnEmpty) {
		  	setActiveFurnitureId(null)
		  	setSelectedContextMenu(null)
		}
	}

	const onChangeTable = (tabs, tableId) => {
		setUpdating(true)
		setTables(tabs)
		const changedTabs = tabs.map(el => ({
			...el,
			height: Number(el.height.toFixed(0)),
			width: Number(el.width.toFixed(0)),
			positionLeft: Number(el.positionLeft.toFixed(0)),
			positionTop: Number(el.positionTop.toFixed(0))
		}))
		setCurrentProjectData({ ...currentProjectData, roomData: { ...currentProjectData.roomData, furniture: changedTabs.concat(interior) } })

		const changedTable = changedTabs.find(el => el.id === tableId)
		updateFurniture({
			variables: {
				fields: {
					id: changedTable.id,
					caption: changedTable.caption,
					positionLeft: changedTable.positionLeft,
					positionTop: changedTable.positionTop,
					shape: changedTable.shape,
					height: changedTable.height,
					width: changedTable.width,
					componentColor: changedTable.componentColor
				}
			}
		}).then(() => {
			setUpdating(false)
		})
	}

	const onChangeInterior = (newAttr) => {
		setUpdating(true)
		const changedObjects = interior.map(el => {
			if (el.id === newAttr.id) {
				return {
					...el,
					...newAttr
				}
			}
			return el
		})

		setInterior(changedObjects)
		setCurrentProjectData({ ...currentProjectData, roomData: { ...currentProjectData.roomData, furniture: changedObjects.concat(tables) } })

		const changedElement = changedObjects.find(el => el.id === newAttr.id)
		updateFurniture({
			variables: {
				fields: {
					id: changedElement.id,
					positionLeft: Number(changedElement.positionLeft.toFixed(0)),
					positionTop: Number(changedElement.positionTop.toFixed(0)),
					rotationDeg: Number(changedElement.rotationDeg.toFixed(0)),
					height: Number(changedElement.height.toFixed(0)),
					width: Number(changedElement.width.toFixed(0))
				}
			}
		}).then(() => {
			setUpdating(false)
		})
	}

	const handleOptionSelected = option => {
		setSelectedContextMenu(null)
		if (option === 'delete') {
			mutation(deleteFurniture, { ids: [activeFurnitureId] })
			setActiveFurnitureId(null)
		}
		if (option === 'copy') {
			const element = currentProjectData.roomData.furniture.find(el => el.id === activeFurnitureId)

			const fields = {
				projectId: element.projectId,
				floorId: element.floorId,
				roomId: element.roomId,
				componentColor: element.objectKind === 'table' ? element.componentColor : null,
				height: element.height,
				width: element.width,
				shape: element.shape,
				objectType: element.objectType,
				objectKind: element.objectKind,
				caption: element.objectKind === 'table' ? element.caption : null
			}
			mutation(cloneFurniture, { fields: [fields] })
		}
	}

	const handleContextMenu = e => {
		e.evt.preventDefault(true)
		const mousePosition = e.target.getStage().getPointerPosition()

		setSelectedContextMenu({
			position: mousePosition
		})
	}

	return (
		<StyledDnDContainer id='room' color={currentProjectData.roomData.roomColor}>
			<Stage
				width={canvasWidth}
				height={canvasHeight}
				onMouseDown={checkDeselect}
	  			onTouchStart={checkDeselect}
			>
				<Layer>
					{linesA}
					{linesB}
				</Layer>
				<Layer>
					{interior?.filter(el => el.objectKind !== 'plant')?.map((el, i) => {
						return (
							<Interior
								key={el.id}
								shapeProps={el}
								isSelected={el.id === activeFurnitureId}
								grid={gridSize}
								onSelect={() => {
									setActiveFurnitureId(el.id)
									setShapeCreatorType('interior')
								}}
								onChange={onChangeInterior}
								onContextMenu={(e) => {
									setActiveFurnitureId(el.id)
									setShapeCreatorType('interior')
									handleContextMenu(e)
								}}
							/>
						)
					})}
					{tables.map((table, i) => {
						return (
							<Table
								key={table.id}
								shapeProps={table}
								isSelected={table.id === activeFurnitureId}
								grid={gridSize}
								onSelect={() => {
									setActiveFurnitureId(table.id)
									setShapeCreatorType('table')
								}}
								onChange={(newAttrs) => {
									const tabs = tables.slice()
									tabs[i] = newAttrs
									onChangeTable(tabs, newAttrs.id)
								}}
            					onContextMenu={(e) => {
									setActiveFurnitureId(table.id)
									setShapeCreatorType('table')
									handleContextMenu(e)
								}}
							/>
						)
					})}
					{interior?.filter(el => el.objectKind === 'plant')?.map((el, i) => {
						return (
							<Interior
								key={el.id}
								shapeProps={el}
								isSelected={el.id === activeFurnitureId}
								grid={gridSize}
								onSelect={() => {
									setActiveFurnitureId(el.id)
									setShapeCreatorType('interior')
								}}
								onChange={onChangeInterior}
								onContextMenu={(e) => {
									setActiveFurnitureId(el.id)
									setShapeCreatorType('interior')
									handleContextMenu(e)
								}}
							/>
						)
					})}
					{selectedContextMenu && (
						<Portal>
							<ContextMenu
								{...selectedContextMenu}
								menuOptions={['copy', 'delete']}
								onOptionSelected={handleOptionSelected}
							/>
						</Portal>
					)}
				</Layer>
			</Stage>
		</StyledDnDContainer>
	)
}

export default Container
